/**
 * "Log in, then carry on where you were" for customer actions that need an
 * account (currently: adding / editing a delivery address).
 *
 * Before sending someone to the login screen, the caller records where to
 * come back to and what they were doing (plus any address they had already
 * typed). The login screens send a customer back to that page, and the page
 * picks the action up again. Kept in sessionStorage (this tab only) with a
 * short expiry, so an abandoned login can't hijack a much later one.
 */

const KEY = 'login_intent';
export const LOGIN_INTENT_TTL_MS = 10 * 60 * 1000;

const safeStorage = () => {
  try { return window.sessionStorage; } catch (_) { return null; }
};

// Same-app paths only — never an absolute or protocol-relative URL, and never
// back to a login screen.
const isSafeReturnPath = (path) => typeof path === 'string'
  && path.startsWith('/') && !path.startsWith('//') && !path.includes('://')
  && !/^\/(login|rider)(\/|\?|$)/.test(path);

const pathnameOf = (path) => path.split(/[?#]/)[0];

const read = (storage, now) => {
  try {
    const intent = JSON.parse(storage.getItem(KEY) || 'null');
    if (!intent || !isSafeReturnPath(intent.returnTo)) return null;
    if (!(now - intent.at >= 0 && now - intent.at < LOGIN_INTENT_TTL_MS)) return null;
    return intent;
  } catch (_) {
    return null;
  }
};

/**
 * Remember what to resume after login. `action` / `draft` are optional.
 * `keepSamePage`: don't replace a pending intent for the same page — used by
 * a route guard that only knows "come back here", so it can't wipe a more
 * specific one (e.g. an edit in progress) saved just before it ran.
 */
export function saveLoginIntent({ returnTo, action = null, draft = null }, { storage = safeStorage(), now = Date.now(), keepSamePage = false } = {}) {
  if (!storage || !isSafeReturnPath(returnTo)) return false;
  if (keepSamePage) {
    const existing = read(storage, now);
    if (existing && pathnameOf(existing.returnTo) === pathnameOf(returnTo)) return true;
  }
  try {
    storage.setItem(KEY, JSON.stringify({ returnTo, action, draft, at: now }));
    return true;
  } catch (_) {
    return false;
  }
}

/** Where a customer should land after logging in, or null. Doesn't consume it. */
export function loginReturnPath({ storage = safeStorage(), now = Date.now() } = {}) {
  if (!storage) return null;
  return read(storage, now)?.returnTo || null;
}

/**
 * On the page the customer was sent back to: take the saved intent if it
 * belongs to this page (same pathname), removing it so it runs once.
 * Returns { action, draft } or null.
 */
export function takeLoginIntent(pathname, { storage = safeStorage(), now = Date.now() } = {}) {
  if (!storage) return null;
  const intent = read(storage, now);
  if (!intent) {
    try { storage.removeItem(KEY); } catch (_) { /* nothing to clear */ }
    return null;
  }
  if (pathnameOf(intent.returnTo) !== pathname) return null;
  try { storage.removeItem(KEY); } catch (_) { /* best effort */ }
  return { action: intent.action, draft: intent.draft };
}

// What the API answers when the request carries no valid login (FastAPI's
// HTTPBearer without a token, or routes.auth_utils for a bad/expired/revoked
// token). Other 401s — a deactivated or deleted account — are real account
// problems whose own message should be shown instead.
const SESSION_401_DETAILS = new Set(['Token expired', 'Invalid token', 'Session expired. Please login again.']);

/** True when a failed request means "you're not (or no longer) logged in". */
export function isSessionAuthError(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 403 && detail === 'Not authenticated') return true;
  return status === 401 && SESSION_401_DETAILS.has(detail);
}
